import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { toast } from "sonner";
import * as authLib from "@/lib/auth";
import { emitAuthEvent } from "@/lib/auth-events";
import {
  NOT_PROJECT_MEMBER,
  NOT_PROJECT_MEMBER_FALLBACK,
  NOT_PROJECT_MEMBER_TOAST_ID,
} from "@/lib/project-permissions";

interface ApiErrorBody {
  code?: string;
  message?: string;
}

/** Backend code (packages/shared/middleware/authoritative-role.ts) for a deactivated caller. */
export const ACCOUNT_DEACTIVATED = "ACCOUNT_DEACTIVATED";

/** True when the server rejected the request because the CALLER's account is deactivated. */
export function isAccountDeactivated403(
  error: AxiosError<ApiErrorBody>,
): boolean {
  return (
    error.response?.status === 403 &&
    error.response?.data?.code === ACCOUNT_DEACTIVATED
  );
}

/** Machine-readable `code` from an API error body, if any. */
export function apiErrorCode(err: unknown): string | undefined {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiErrorBody | undefined;
    return typeof data?.code === "string" ? data.code : undefined;
  }
  return undefined;
}

/**
 * Human-readable message for an API error: the server's `message` when present (e.g. the
 * SELF_ROLE_CHANGE_FORBIDDEN / LAST_LEADERSHIP_USER explanations), else `fallback`. Never the
 * generic axios "Request failed with status code 403".
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiErrorBody | undefined;
    if (typeof data?.message === "string" && data.message) return data.message;
    return fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

// Allow callers to opt out of the global error toast for best-effort requests that
// degrade gracefully in the UI (e.g. the reviewer directory fetch). Module augmentation
// keeps this type-safe wherever an AxiosRequestConfig is accepted.
declare module "axios" {
  export interface AxiosRequestConfig {
    _suppressErrorToast?: boolean;
  }
}

// Extend Axios config to track retry state — prevents infinite retry loops
interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  /** Set once a role-denied 403 has triggered its single forced refresh + retry. */
  _roleRetry?: boolean;
}

/**
 * Matches the backend RBAC denial (`Role 'x' is not permitted to access this resource` from
 * packages/shared/middleware/rbac.ts). Only this 403 is worth a token refresh — it may mean the
 * ID token still carries a role that was changed server-side. Other 403s (ownership, self-role
 * change, etc.) are business rules a fresh token cannot fix.
 */
const ROLE_DENIED_PATTERN = /is not permitted to access this resource/i;

export function isRoleDenied403(error: AxiosError<ApiErrorBody>): boolean {
  return (
    error.response?.status === 403 &&
    ROLE_DENIED_PATTERN.test(error.response?.data?.message ?? "")
  );
}

/** Safely extract a human-readable message from an error response body. */
function serverMessage(
  error: AxiosError<ApiErrorBody>,
  fallback: string,
): string {
  return error.response?.data?.message ?? fallback;
}

let axiosInstance: AxiosInstance | null = null;
let governingEventsAxiosInstance: AxiosInstance | null = null;
let provisioningAxiosInstance: AxiosInstance | null = null;

function createAxiosInstance(baseURL?: string): AxiosInstance {
  const instance = axios.create({
    baseURL: baseURL ?? import.meta.env.VITE_API_BASE_URL,
    timeout: 10000,
  });

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // API Gateway Cognito Authorizer validates the ID token, not the access token
      const token = authLib.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiErrorBody>) => {
      const status = error.response?.status;
      const config = error.config as RetryableConfig | undefined;

      // 401 — attempt ONE silent token refresh, then retry the original request once.
      // _retry flag prevents infinite loop if the retried request also gets 401.
      if (status === 401 && config && !config._retry) {
        config._retry = true;

        const refreshed = await authLib.refreshTokens();
        if (refreshed) {
          const token = authLib.getIdToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          // Single retry with new token — if this also 401s, _retry is set so we fall through
          return axiosInstance!.request(config);
        } else {
          // Refresh failed — session is dead, redirect to login
          authLib.logout();
          window.location.href = "/login";
          return Promise.reject(error);
        }
      }

      // Deactivated caller — the server (DB-authoritative) says this account is off. No retry can
      // fix it: end the session (AuthContext logs out and clears the Cognito localStorage keys).
      if (isAccountDeactivated403(error)) {
        emitAuthEvent("account-deactivated");
        return Promise.reject(error);
      }

      // Role-denied 403 — authorization is DB-authoritative, so the UI may be rendering a role the
      // server no longer grants. Ask AuthContext to re-fetch the server role (GET
      // /api/users/manage?self=true) so role-gated UI converges immediately. Then force ONE token
      // refresh and retry once (_roleRetry guards against loops) — the refresh still matters
      // because a role change revokes the session globally, in which case re-login is needed.
      if (isRoleDenied403(error)) {
        emitAuthEvent("authz-denied");
      }
      if (config && !config._roleRetry && isRoleDenied403(error)) {
        config._roleRetry = true;
        const refreshed = await authLib.refreshTokens();
        if (refreshed) {
          const token = authLib.getIdToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return instance.request(config);
        }
        authLib.logout();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      // If we're here on a 401 it means the retry also failed — log out
      if (status === 401) {
        authLib.logout();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      // Callers may opt out of the global error toast (best-effort fetches that degrade
      // gracefully in the UI) by setting `_suppressErrorToast` on the request config.
      const suppressErrorToast = config?._suppressErrorToast === true;

      // Network / no-response errors (timeouts, DNS, offline).
      if (!error.response) {
        if (!suppressErrorToast)
          toast.error("Network error. Check your connection.");
        return Promise.reject(error);
      }

      if (suppressErrorToast) {
        return Promise.reject(error);
      }

      // Map HTTP status codes to human-readable toasts.
      switch (status) {
        case 400:
          toast.error(
            "Invalid request: " + serverMessage(error, "Bad request"),
          );
          break;
        case 403:
          // Membership denials carry an actionable server message ("Only members of this project
          // can …") — show it instead of the generic copy.
          if (error.response?.data?.code === NOT_PROJECT_MEMBER) {
            toast.error(serverMessage(error, NOT_PROJECT_MEMBER_FALLBACK), {
              id: NOT_PROJECT_MEMBER_TOAST_ID,
            });
          } else {
            toast.error("You don't have permission to do this");
          }
          break;
        case 404:
          toast.error("Resource not found");
          break;
        case 409:
          toast.error(serverMessage(error, "Conflict with the current state"));
          break;
        case 422:
          toast.error(serverMessage(error, "Validation failed"));
          break;
        case 500:
        case 502:
          toast.error("Server error. Please try again.");
          break;
        default:
          break;
      }

      return Promise.reject(error);
    },
  );

  return instance;
}

/** The shared main-API axios instance (non-hook accessor for plain modules, e.g. server-role). */
export function getApiClient(): AxiosInstance {
  if (!axiosInstance) {
    axiosInstance = createAxiosInstance();
  }
  return axiosInstance;
}

export function useApiClient(): AxiosInstance {
  return getApiClient();
}

/** Axios instance pointed at the GoverningEvents API (separate API GW, same auth). */
export function useGoverningEventsClient(): AxiosInstance {
  if (!governingEventsAxiosInstance) {
    const baseURL =
      import.meta.env.VITE_GOVERNING_EVENTS_API_URL ??
      import.meta.env.VITE_API_BASE_URL;
    governingEventsAxiosInstance = createAxiosInstance(baseURL);
  }
  return governingEventsAxiosInstance;
}

/**
 * Axios instance for the Provisioning actions API (integration cancel/retry). It is a SEPARATE
 * API Gateway (infra/stacks/provisioning-infra.ts — the shared API's stack is at its CloudFormation
 * resource cap), same Cognito auth. Configure VITE_PROVISIONING_API_URL from the
 * `ProvisioningApiUrl` stack output; falls back to the main API base when unset.
 */
export function useProvisioningClient(): AxiosInstance {
  if (!provisioningAxiosInstance) {
    const baseURL =
      import.meta.env.VITE_PROVISIONING_API_URL ??
      import.meta.env.VITE_API_BASE_URL;
    provisioningAxiosInstance = createAxiosInstance(baseURL);
  }
  return provisioningAxiosInstance;
}

export function useApi() {
  const navigate = useNavigate();
  const client = useApiClient();

  return {
    get: useCallback(
      async (url: string) => {
        try {
          const response = await client.get(url);
          return response.data;
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            navigate("/login");
          }
          throw error;
        }
      },
      [client, navigate],
    ),

    post: useCallback(
      async (url: string, data?: unknown) => {
        try {
          const response = await client.post(url, data);
          return response.data;
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            navigate("/login");
          }
          throw error;
        }
      },
      [client, navigate],
    ),

    patch: useCallback(
      async (url: string, data?: unknown) => {
        try {
          const response = await client.patch(url, data);
          return response.data;
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            navigate("/login");
          }
          throw error;
        }
      },
      [client, navigate],
    ),

    delete: useCallback(
      async (url: string) => {
        try {
          const response = await client.delete(url);
          return response.data;
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            navigate("/login");
          }
          throw error;
        }
      },
      [client, navigate],
    ),

    client,
  };
}
